import { useEffect, useRef, useState } from 'react';
import type { ReactElement, RefObject } from 'react';
import { useNavigate } from 'react-router';

import { LanguageRoute } from '@/config/router/routes';
import type { PersonModel } from '@/domain/models/personModel';
import { appStateStore } from '@/presentation/stores/appStateStore';
import { valueOrNull } from '@/presentation/stores/asyncValue';
import { usePersonGiff } from '@/presentation/stores/usePersonGiff';
import { ShimmerCard } from '@/presentation/widgets/ShimmerCard';
import { toRequestUrl } from '@/utils/proxyUrl';

import styles from './PersonCard.module.css';

/**
 * Перенос `lib/src/presentation/pages/persons/person_card.dart`.
 *
 * `Bounceable` стал обычной кнопкой: нажатие сжимает её через `:active`,
 * а `IgnorePointer` вокруг картинки — `pointer-events: none`, чтобы
 * попадание ловила только кнопка (в Dart это `HitTestBehavior.translucent`).
 */

/** Состояние загрузки `CachedNetworkImage`: заглушка, картинка или ошибка. */
type PictureStatus = 'loading' | 'loaded' | 'error';

/**
 * `context.mounted` после `await`: в Dart по нему решают, уходить ли на
 * другой экран.
 */
function useMounted(): RefObject<boolean> {
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return mounted;
}

/** Подпись для скринридера: во Flutter у картинки её нет, в вебе она обязательна. */
function personLabel(person: PersonModel): string {
  return person.name ?? person.slug;
}

interface PersonPictureProps {
  readonly person: PersonModel;
}

/**
 * `CachedNetworkImage(alignment: Alignment.topCenter, fit: BoxFit.cover)` с
 * `ShimmerCard(expand: true)` и на ожидание, и на ошибку.
 *
 * Кеша, как у `cached_network_image`, тут нет — им занимается сам браузер.
 * Адрес заворачивается в прокси (как и анимация в `usePersonGiff`): узлы
 * аватаров живут на произвольных адресах, и страница по https не покажет
 * картинку с http напрямую.
 */
function PersonPicture({ person }: PersonPictureProps): ReactElement {
  const src = toRequestUrl(person.picture);
  const [status, setStatus] = useState<PictureStatus>('loading');
  const [renderedSrc, setRenderedSrc] = useState(src);
  if (renderedSrc !== src) {
    // Сменился адрес — снова ожидание, прошлая картинка на экране не остаётся.
    setRenderedSrc(src);
    setStatus('loading');
  }

  const imageRef = useRef<HTMLImageElement>(null);
  useEffect(() => {
    const image = imageRef.current;
    if (image === null || !image.complete) return;
    // Картинка из кеша браузера успевает загрузиться до подписки на события —
    // тогда `load` уже не придёт, и состояние читается у самого элемента.
    setStatus(image.naturalWidth > 0 ? 'loaded' : 'error');
  }, [src]);

  const imageClass =
    status === 'loaded' ? styles.picture : `${styles.picture} ${styles.hidden}`;
  return (
    <>
      <img
        ref={imageRef}
        className={imageClass}
        src={src}
        alt={personLabel(person)}
        onLoad={() => setStatus('loaded')}
        onError={() => setStatus('error')}
      />
      {status !== 'loaded' && <ShimmerCard expand />}
    </>
  );
}

export interface PersonCardProps {
  readonly person: PersonModel;
}

export function PersonCard({ person }: PersonCardProps): ReactElement {
  const navigate = useNavigate();
  const mounted = useMounted();
  const giffState = usePersonGiff(person.giff);
  const giffUrl = valueOrNull(giffState);

  const handleTap = async (): Promise<void> => {
    const result = await appStateStore.getState().changePerson(person);
    if (result && mounted.current) LanguageRoute.go(navigate);
  };

  return (
    <button
      type="button"
      className={styles.card}
      onClick={() => {
        void handleTap();
      }}
    >
      {/* ClipRRect(borderRadius: BorderRadius.circular(24)) */}
      <div className={styles.clip}>
        {giffUrl === null ? (
          <PersonPicture key="placeholder" person={person} />
        ) : (
          // `Image.memory` без `alignment` — значит, по центру, в отличие
          // от картинки-заглушки.
          <img
            key={person.id}
            className={styles.giff}
            src={giffUrl}
            alt={personLabel(person)}
          />
        )}
      </div>
    </button>
  );
}
